import { uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { AnnotationTemplate } from '@paragon/contract-library';
import { naturalSortByProperty, notNullish } from '@paragon/utilities';

import { RootState } from '../store';

const getTemplateData = (state: RootState) => state.annotationTemplates.annotationTemplates;
const getDeletedTemplates = (state: RootState) => state.annotationTemplates.deletedTemplates;

const getSelectedTemplateId = (state: RootState) => state.annotationTemplates.selectedTemplate;

export const getAllAnnotationTemplates = createSelector(
  [getTemplateData, getDeletedTemplates],
  (annotationTemplates, deletedTemplates) =>
    Object.values(annotationTemplates).filter((template) => !deletedTemplates.includes(template.id)),
);

export const getSelectedTemplate = createSelector(
  [getAllAnnotationTemplates, getSelectedTemplateId],
  (templates, selectedTemplateId) => templates.find((template) => template.id === selectedTemplateId),
);

export const getSortedAnnotationTemplates = createSelector(getAllAnnotationTemplates, (templates) =>
  naturalSortByProperty(templates, (template) => template.name.toLowerCase()),
);

function sortGroups(groups: string[]) {
  return naturalSortByProperty(groups, (groupName) => groupName.toLowerCase());
}

function getSortedGroupNamesFromTemplates(
  templates: readonly AnnotationTemplate[],
  locallyAddedTemplateGroups: readonly string[],
) {
  if (templates == null) {
    return [];
  }

  const uniqueGroupNames = uniq(templates.map((template) => template.group).concat(locallyAddedTemplateGroups)).filter(
    notNullish,
  );
  return sortGroups(uniqueGroupNames);
}

export const getSortedTemplateGroupNames = createSelector(
  getAllAnnotationTemplates,
  (state: RootState) => state.annotationTemplates.locallyAddedTemplateGroups,
  getSortedGroupNamesFromTemplates,
);

export function getCorrespondingLandscapeTemplate(portraitTemplate: string, state: RootState) {
  const allTemplates = Object.values(state.annotationTemplates.annotationTemplates);

  if (allTemplates.find((template) => template.name === portraitTemplate) == null) {
    return '';
  }

  let desiredName = portraitTemplate;
  if (desiredName.endsWith('Portrait')) {
    desiredName = desiredName.substring(0, desiredName.lastIndexOf('Portrait'));
  }

  const foundTemplate = allTemplates.find(
    (template) => template.name.startsWith(desiredName) && template.name.endsWith('Landscape'),
  );

  return foundTemplate ? foundTemplate.name : portraitTemplate;
}
